import { useCallback, useEffect, useRef, useState } from "react";

const POLL_MS = 60_000;

/**
 * Polls GET /api/changes/unread-count on mount and every 60s. Works for either
 * an authenticated tenant (storesync_session cookie) or an admin (admin_session
 * cookie) — both ride the fetch via credentials:"include". On any error (e.g.
 * 401 for a standalone visitor with no session) the count is treated as 0 so
 * the badge simply stays hidden.
 */
export function useUnreadChanges(): {
  unread: number;
  refresh: () => void;
  markViewed: () => Promise<void>;
} {
  const [unread, setUnread] = useState(0);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/changes/unread-count", {
        credentials: "include",
      });
      if (!res.ok) {
        if (mounted.current) setUnread(0);
        return;
      }
      const j = (await res.json()) as { unread?: number };
      if (mounted.current) setUnread(typeof j.unread === "number" ? j.unread : 0);
    } catch {
      if (mounted.current) setUnread(0);
    }
  }, []);

  const markViewed = useCallback(async () => {
    try {
      await fetch("/api/changes/mark-viewed", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
    } catch {
      // Non-fatal: a failed mark-viewed just leaves the badge until next poll.
    }
    await refresh();
  }, [refresh]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [refresh]);

  return { unread, refresh, markViewed };
}
